import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import { toast } from 'react-toastify';
import api from '../../../../api/api';
import classes from './addScripts.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import DOBPicker from '../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import Select from '../../../_common/Form/Selects/Select/Select';
import Checkbox from '../../../_common/Form/Checkbox/Checkbox';
import PatientInfo from './PatientInfo/PatientInfo';
import Medication from './Medication/Medication';
import PhysicianInfo from './Physician/Physician';
import PriorAuthorization from './PriorAuthorization/PriorAuthorization';
import Processing from './Processing/Processing';
import Shipping from './Shipping/Shipping';
import OTC from './OTC/OTC';
import accountHelpers from '../../../../utils/accountHelpers';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';
import Loader from '../../../_common/Loader/Loader';
import InputField from '../../../_common/Form/TextFields/InputField';
import utils from '../../../../utils/utils';
import TimePicker from '../../../_common/Form/DatePickers/TimePicker/TimePicker';

const statusOptions = [
  'Received',
  'Review',
  'Prior Auth',
  'Process',
  'Copay Assistance',
  'Schedule',
  'QA',
  'Fill',
  'Shipped',
  'Done',
  'Cancelled',
  'Refill',
  'Renew',
];

const productChangeOldStatuses = ['Shipped', 'Done'];
const productChangeNewStatuses = ['Received', 'Review', 'Prior Auth', 'Process', 'Copay Assistance', 'Schedule',
  'QA', 'Fill'];

const scriptDataTemplate = {
  pouch: false,
  pharmNPI: '',
  location: '',
  pharmDate: '',
  salesCode: '',
  cost: '',
  rxNumber: '',
  primInsPay: '',
  secInsPay: '',
  patientPay: '',
  refills: '',
  refillsRemaining: '',
  quantity: '1',
  daysSupply: '',
  directions: '',
  priorAuth: 'Approved',
  homeCare: false,
  processedOn: moment().format('MM/DD/YYYY'),
  status: 'Received',
  PhysicianId: '',
  value: '',
  writtenDate: '',
  billOnDate: '',
  shipOn: '',
  ETA: '',
  copayApproval: '',
  copayNetwork: '',
  networkPay: '',
  diagnosis: '',
  secDiagnosis: '',
  doNotRefill: false,
  physSearch: false,
  validationMessage: '',
  productsSelected: [],
  Physician: null,
};

const xphozanIds = [5880, 5881];

const AddScript = () => {
  const { patientId, scriptId } = useParams();
  const navigate = useNavigate();

  const [scriptLoading, setScriptLoading] = useState(true);

  const [scriptData, setScriptData] = useState(scriptDataTemplate);
  const [scriptSaveLoading, setScriptSaveLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [productModalInfo, setProductModalInfo] = useState({});
  const [productSaveLoading, setProductSaveLoading] = useState(false);

  const {
    pouch, pharmNPI, location, pharmDate, salesCode, cost, rxNumber, primInsPay, secInsPay, patientPay,
    refills, refillsRemaining, quantity, daysSupply, directions, priorAuth, homeCare, processedOn, status,
    PhysicianId, writtenDate, billOnDate, shipOn, ETA, copayApproval, copayNetwork, networkPay, diagnosis,
    secDiagnosis, doNotRefill, productsSelected, patientName, patientDob, patientPhone, patientAddressStreet,
    patientAddressCity, patientAddressState, patientZipCode, Physician, deliveryMethod, paymentOption, trackNum,
    hcHome, hcPhone, productCost, productPackageSize, UserId, id, cancelReason, transNPI, transDate, ProductId,
    productQuantity, oldStatus, productName, oldProcessedOn, PatientId, ardelyxId, ardelyxDate, ardelyxTime, copay_Card,
  } = scriptData;

  useEffect(() => {
    (async () => {
      setScriptLoading(true);

      if (patientId) {
        const { data: { data: { response } } } = await api.getPatients({ patientId });

        const patient = response[0];

        setScriptData((prev) => ({
          ...prev,
          PatientId: patient.id,
          PrimId: patient.PrimId || null,
          SecId: patient.SecId || null,
          patientName: `${patient.firstName} ${patient.lastName}`,
          patientDob: moment(patient.dob).isValid() ? moment(patient.dob).format('MM.DD.YYYY') : '-',
          patientPhone: patient.phone,
          patientAddressStreet: patient.addressStreet,
          patientAddressCity: patient.addressCity,
          patientAddressState: patient.addressState,
          patientZipCode: patient.addressZipCode,
        }));
      } else {
        const { data: { response } } = await api.getScripts({
          scriptId, userId: accountHelpers.getUser()?.id,
        });

        const productCurrentCost = Number.isFinite(+response[0]?.Product?.cost.replace(',', ''))
          ? response[0]?.Product?.cost.replace(',', '')
          : 0;

        const calculatedCost = updateCost(+response[0].quantity, productCurrentCost, response[0]?.Product?.packageSize);

        const script = {
          ...response[0],
          oldStatus: response[0].status,
          oldProcessedOn: response[0].processedOn,
          patientName: `${response[0].Patient.firstName} ${response[0].Patient.lastName}`,
          patientDob: response[0].Patient.dob,
          patientPhone: response[0].Patient.phone,
          patientAddressStreet: response[0].Patient.addressStreet,
          patientAddressCity: response[0].Patient.addressCity,
          patientAddressState: response[0].Patient.addressState,
          patientZipCode: response[0].Patient.addressZipCode,
          Physician: {
            ...response[0].Physician,
            name: `${response[0].Physician?.firstName} ${response[0].Physician?.lastName}`,
            address: _.compact([response[0].Physician?.addressStreet, response[0].Physician?.addressCity, response[0].Physician?.addressState, response[0].Physician?.addressZipCode]).join(', '),
          },
          productsSelected: [response[0].Product],
          productCost: productCurrentCost,
          productPackageSize: response[0]?.Product?.packageSize,
          productQuantity: response[0]?.Product?.quantity,
          productName: response[0]?.Product?.name,
          // cost:productCurrentCost
          cost: calculatedCost,
        };

        if (xphozanIds.includes(+response[0].ProductId)) {
          const date = moment(response[0]?.ardelyxDate).isValid() ? moment.utc(response[0]?.ardelyxDate).local().format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
          const time = moment(response[0]?.ardelyxDate).isValid() ? moment.utc(response[0]?.ardelyxDate).local().format('hh:mm A') : moment().format('hh:mm A');

          script.ardelyxDate = date;
          script.ardelyxTime = time;
          script.ardelyxId = response[0]?.ardelyxId || '';
        }

        setScriptData(script);
      }

      setScriptLoading(false);
    })();
  }, []);

  const changeScriptData = useCallback((path, data) => {
    setErrors((prev) => {
      delete prev[path];

      return prev;
    });

    if (path === 'productsSelected') {
      // if (scriptId) {
      setErrors((prev) => {
        delete prev.ardelyxTime;
        delete prev.ardelyxDate;
        delete prev.ardelyxId;

        return prev;
      });

      setScriptData((prev) => {
        if (prev.productsSelected.find((p) => p.id === data.id)) {
          delete prev.ardelyxTime;
          delete prev.ardelyxDate;
          delete prev.copay_Card;

          return { ...prev, productsSelected: [], ProductId: null };
        }

        const newData = {
          ...prev,
          productsSelected: [data],
          ProductId: data.id,
          productPackageSize: data.packageSize,
          productCost: data.cost,
          cost: updateCost(quantity, data.cost, data.packageSize),
        };

        if (xphozanIds.includes(+data.id)) {
          const date = prev?.ardelyxDate?.length === 10 ? prev.ardelyxDate : moment().format('YYYY-MM-DD');
          const time = prev?.ardelyxTime?.length === 8 ? prev.ardelyxTime : moment().format('hh:mm A');

          newData.ardelyxDate = date;
          newData.ardelyxTime = time;
          newData.ardelyxId = prev?.ardelyxId || '';
        }

        return { ...newData };
      });
      // } else {
      //   setScriptData((prev) => {
      //     let newSelectedProducts = [...prev.productsSelected, data];
      //
      //     if (prev.productsSelected.find((p) => p.id === data.id)) {
      //       newSelectedProducts = prev.productsSelected.filter((p) => p.id !== data.id);
      //     }
      //
      //     return {
      //       ...prev,
      //       productsSelected: newSelectedProducts,
      //       ProductId: data.id,
      //       productPackageSize: data.packageSize,
      //       productCost: data.cost,
      //       cost: updateCost(quantity, data.cost, data.packageSize),
      //     };
      //   });
      // }
    } else if (path === 'quantity') {
      setScriptData((prev) => ({
        ...prev, [path]: data, cost: updateCost(data, productCost, productPackageSize || 1),
      }));
    } else if (path === 'patient') {
      setScriptData((prev) => ({ ...prev, ...data }));
    } else if (path === 'Physician') {
      setScriptData((prev) => ({ ...prev, PhysicianId: data.id, [path]: data }));
    } else if (path === 'copayApproval' && data !== 'Approved') {
      setScriptData((prev) => ({
        ...prev,
        copayApproval: data,
        copayNetwork: '',
        networkPay: '',
      }));
    } else {
      setScriptData((prev) => ({ ...prev, [path]: data }));
    }
  }, [scriptData]);

  const updateCost = (qty, price, packageSize) => {
    const currentCost = (+price / +packageSize) * +qty;
    return Math.floor((currentCost) * 100) / 100 || 0;
  };

  const saveScript = useCallback(async (callFromModal) => {
    let hasError = false;

    const dateFields = ['pharmDate', 'processedOn', 'writtenDate', 'billOnDate', 'shipOn', 'ETA'];
    const shipOnRequiredStatuses = ['QA', 'Fill', 'Shipped'];

    _.forEach(scriptData, (val, key) => {
      if ((key === 'productsSelected' || key === 'Physician') && _.isEmpty(val)) {
        setErrors((prev) => ({ ...prev, [key]: 'Field is required' }));
        hasError = true;
      }

      if (key === 'quantity' && +val <= 0) {
        setErrors((prev) => ({ ...prev, [key]: 'Quantity must be greater than 0' }));
        hasError = true;
      }

      if (key === 'processedOn' && !val) {
        setErrors((prev) => ({ ...prev, [key]: 'Field is required' }));

        hasError = true;
      }

      if (shipOnRequiredStatuses.includes(status) && key === 'shipOn' && !val) {
        setErrors((prev) => ({ ...prev, [key]: 'Field is required' }));

        hasError = true;
      }

      if (dateFields.includes(key) && val && val.length !== 10) {
        setErrors((prev) => ({ ...prev, [key]: 'Invalid date' }));

        hasError = true;
      }

      if (xphozanIds.includes(+ProductId)) {
        if (key === 'ardelyxDate' && val && val.length !== 10) {
          setErrors((prev) => ({ ...prev, [key]: 'Invalid date' }));

          hasError = true;
        } else if (key === 'ardelyxTime' && val && val.length !== 8) {
          setErrors((prev) => ({ ...prev, [key]: 'Invalid date' }));

          hasError = true;
        }
      }
    });

    if (hasError) {
      toast.error('Please fill this fields');
    } else {
      try {
        if (callFromModal) setScriptSaveLoading(true);

        if (scriptId) {
          const sendData = {
            id,
            userId: UserId,
            PatientId,
            PhysicianId,
            processedOn,
            pouch,
            status,
            cancelReason,
            priorAuth,
            location,
            transNPI,
            transDate,
            writtenDate,
            salesCode,
            billOnDate,
            cost,
            rxNumber,
            primInsPay,
            diagnosis,
            secInsPay,
            secDiagnosis,
            patientPay,
            refills,
            refillsRemaining,
            quantity,
            daysSupply,
            directions,
            copayApproval,
            copayNetwork,
            homeCare,
            hcHome,
            hcPhone,
            doNotRefill,
            copay_Card,
            ProductId: ProductId || undefined,
            shipOn: shipOn || undefined,
            ETA: ETA || undefined,
            deliveryMethod: deliveryMethod || undefined,
            trackNum: trackNum || undefined,
            networkPay: networkPay || undefined,
            paymentOption: paymentOption || undefined,

          };

          if (xphozanIds.includes(+ProductId)) {
            sendData.ardelyxDate = moment.utc(new Date(`${ardelyxDate} ${ardelyxTime}`)).format('YYYY-MM-DD HH:mm:ss');
            sendData.ardelyxId = ardelyxId;
          } else {
            delete sendData.copay_Card;
          }

          if (!callFromModal && productChangeOldStatuses.includes(oldStatus)
            && productChangeNewStatuses.includes(status)) {
            const newQuantity = (+productQuantity) + (+quantity);

            const text = `Changing this script from ${oldStatus} to ${status} will add ${quantity} back to ${productName}. Continue?\n
              ${productQuantity} + ${quantity} => ${newQuantity} (new on hand quantity)`;

            setProductModalInfo({
              id: ProductId,
              quantity: newQuantity,
              name: productName,
              text,
            });

            return;
          }

          if (status !== oldStatus || processedOn !== oldProcessedOn) {
            const { link, name } = accountHelpers.getUser();
            const statusData = {
              scriptId,
              userId: UserId,
              userImage: link,
              name,
              fromStatus: oldStatus,
              toStatus: status,
              fromProcessedOn: oldProcessedOn,
              toProcessedOn: processedOn,
              manuallyChanged: true,
            };

            await Promise.all([api.updateScriptStatuses(statusData), await api.updateScript(sendData)]);
          } else {
            await api.updateScript(sendData);
          }

          navigate(`/scripts/${scriptId}`);
        } else {
          const sendData = { ...scriptData };

          if (xphozanIds.includes(+ProductId)) {
            sendData.ardelyxDate = moment.utc(new Date(`${ardelyxDate} ${ardelyxTime}`)).format('YYYY-MM-DD HH:mm:ss');
            sendData.ardelyxId = ardelyxId;
          } else {
            delete sendData.ardelyxDate;
            delete sendData.ardelyxId;
            delete sendData.copay_Card;
          }

          delete sendData.ardelyxTime;

          await api.addScript({ ...sendData, UserId: accountHelpers.getUser()?.id });
          navigate(`/patients/${patientId}`);
        }

        toast.success(`Script successfully ${scriptId ? 'updated' : 'added'}`);
        setScriptSaveLoading(false);
      } catch (e) {
        if (e.response) {
          let fullMessage = 'Something went wrong!';

          if (e.response?.data?.error?.details?.length) {
            fullMessage = e.response.data.error.details
              .map((x) => x.message.replace(/([a-z])([A-Z])/g, '$1 $2').concat('. '))[0];
          } else if (e.response?.data?.message) {
            fullMessage = e.response.data.message;
          }

          toast.error(fullMessage);
          setScriptSaveLoading(false);
        }
      }
    }
  }, [scriptData]);

  const saveProduct = useCallback(async () => {
    const sendData = { ...productModalInfo };
    delete sendData.text;

    setProductSaveLoading(true);

    await Promise.all([api.updateProduct(sendData), saveScript(true)]);

    setProductSaveLoading(false);
    setProductModalInfo({});
  }, [productModalInfo]);

  const patientInfo = useMemo(() => ({
    patientName,
    patientDob,
    patientPhone,
    patientAddress: _.compact([patientAddressStreet, patientAddressCity, patientAddressState,
      patientZipCode]).join(', ') || '-',
  }), [patientName, patientDob, patientPhone, patientAddressStreet, patientAddressCity,
    patientAddressState, patientZipCode]);

  const medicationInfo = useMemo(() => productsSelected, [productsSelected]);

  const priorAuthInfo = useMemo(() => ({
    priorAuth,
    pharmNPI,
    location,
    pharmDate,
  }), [priorAuth, pharmNPI, location, pharmDate]);

  const processingInfo = useMemo(() => ({
    writtenDate,
    billOnDate,
    rxNumber,
    diagnosis,
    secDiagnosis,
    refills,
    refillsRemaining,
    quantity,
    daysSupply,
    directions,
    salesCode,
    cost,
    primInsPay,
    secInsPay,
    patientPay,
    copayNetwork,
    copayApproval,
    networkPay,
    copay_Card,
  }), [writtenDate, billOnDate, rxNumber, diagnosis, secDiagnosis, refills, refillsRemaining, quantity, daysSupply,
    directions, salesCode, cost, primInsPay, secInsPay, patientPay, copayNetwork, copayApproval, networkPay, copay_Card]);

  const shippingInfo = useMemo(() => ({
    shipOn, deliveryMethod, paymentOption, ETA, trackNum,
  }), [shipOn, deliveryMethod, paymentOption, ETA, trackNum]);

  const OTCInfo = useMemo(() => ({
    homeCare, hcHome, hcPhone,
  }), [homeCare, hcHome, hcPhone]);

  return (
    <div className={classes.add_content}>
      {scriptLoading
        ? <Loader />

        : (
          <div className={classes.add_wrapper}>
            <div className={classes.header}>
              <h2>{scriptId ? 'Edit Script' : 'Add A New Script'}</h2>

              <div>
                <Button
                  title={`${scriptId ? 'Update' : 'Create'} script`}
                  addButton={!scriptId}
                  onClick={() => saveScript()}
                  reverseColor
                  loading={scriptSaveLoading}
                />

                <Button
                  title="Cancel"
                  href={scriptId ? `/scripts/${scriptId}` : '/patients'}
                  clearButton
                />
              </div>
            </div>

            <div className={classes.fields_wrapper}>
              <div className={classes.process_on_wrapper}>
                <div>
                  <DOBPicker
                    label="Process On"
                    date={processedOn}
                    hideClearButton
                    dateFormat="YYYY-MM-DD"
                    onChange={(date) => changeScriptData('processedOn', date)}
                    autoValidation={false}
                    error={errors.processedOn}
                  />

                  <Checkbox
                    label="SortPak Patient"
                    checked={pouch}
                    className={classes.pouch}
                    onChange={(check) => changeScriptData('pouch', check)}
                  />

                  {xphozanIds.includes(+ProductId) && (
                    <div className={classes.ardelyx_assist}>
                      <div className={classes.ardelyx_assist_label}>ArdelyxAssist</div>

                      <DOBPicker
                        label="Receipt Date"
                        date={ardelyxDate}
                        hideClearButton
                        dateFormat="YYYY-MM-DD"
                        onChange={(date) => changeScriptData('ardelyxDate', date)}
                        autoValidation={false}
                        error={errors.ardelyxDate}
                      />

                      <TimePicker
                        wrapperClassName={classes.time_picker}
                        label="Time"
                        hideClearButton
                        time={ardelyxTime}
                        onChange={(timeValue) => changeScriptData('ardelyxTime', timeValue)}
                        autoValidation={false}
                        error={errors.ardelyxTime}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Select
                    label="Status"
                    options={statusOptions}
                    currentOption={status}
                    hideClearButton
                    onChange={(option) => changeScriptData('status', option)}
                  />

                  <Button
                    title={doNotRefill ? 'Allow refill' : 'Do not refill'}
                    reverseColor
                    buttonColor={doNotRefill ? '#547EEE' : 'red'}
                    onClick={() => changeScriptData('doNotRefill', !doNotRefill)}
                  />

                  {xphozanIds.includes(+ProductId) && (
                    <div className={classes.ardelyx_assist}>
                      <InputField
                        label="Case ID"
                        value={ardelyxId}
                        onChange={(value) => changeScriptData('ardelyxId', value)}
                      />
                    </div>
                  )}
                </div>

              </div>

              <PatientInfo
                patientInfo={patientInfo}
                showInput={!!scriptId}
                setPatient={changeScriptData}
                error={errors.patient}
              />

              <Medication
                medicationInfo={medicationInfo}
                setMedication={changeScriptData}
                error={errors.productsSelected}
              />

              <PhysicianInfo
                physicianInfo={Physician}
                setPhysician={changeScriptData}
                error={errors.Physician}
              />

              <PriorAuthorization
                priorAuthInfo={priorAuthInfo}
                setPriorAuth={changeScriptData}
                error={errors.pharmDate || ''}
              />

              <Processing
                errors={errors}
                showCopayCardField={xphozanIds.includes(+ProductId)}
                processingInfo={processingInfo}
                setProcessing={changeScriptData}
              />

              <Shipping
                shippingInfo={shippingInfo}
                setShipping={changeScriptData}
                errors={errors}
              />

              <OTC
                OTCInfo={OTCInfo}
                setOTC={changeScriptData}
              />
            </div>

            <AttentionModal
              onClose={() => setProductModalInfo({})}
              isOpen={!_.isEmpty(productModalInfo)}
              onSave={saveProduct}
              title={productModalInfo.title}
              text={productModalInfo.text}
              loading={productSaveLoading}
            />
          </div>
        )}
    </div>

  );
};

export default AddScript;
